import React from 'react';
import I18n from 'i18n-js';
import { get } from 'lodash';
import gql from 'graphql-tag';

import ListingShippingDisplay, { ListingShippingDisplayFragment } from './listing_shipping_display';
import PriceDisplay from './price_display';
import RibbonView from './ribbon_view';
import { withUserContext, IUserContext } from './user_context_provider';
import {
  CartItem,
  core_apimessages_Listing_ListingType,
  Item2PriceWithShippingData,
} from '../rql';

interface IExternalProps {
  listing: Item2PriceWithShippingData.Fragment;
  inlineRibbon?: boolean;
  showCombinedRate?: boolean;
  useCombinedShippingStrikethrough?: boolean;
  cartItems?: CartItem[];
}

type IProps = IExternalProps & IUserContext;

export const Item2PriceWithShippingCartItemFragment = gql`
  fragment Item2PriceWithShippingCartItemData on rql_Me {
    cartItems {
      _id
      shopId
      listingId
      subtotal {
        amountCents
        currency
      }
    }
  }
`;

export const Item2PriceWithShippingFragment = gql`
  fragment Item2PriceWithShippingData on Listing {
    _id
    id
    state
    stateDescription
    taxIncluded
    taxIncludedHint
    listingType
    pricing {
      originalPrice {
        display
      }
      ribbon {
        display
      }
      buyerPrice {
        amountCents
        currency
        display
      }
    }
    shop {
      _id
      id
    }
    ...ListingShippingDisplayData
  }
  ${ListingShippingDisplayFragment}
`;

export class PriceWithShipping extends React.Component<IProps, null> {
  get taxIncluded(): boolean {
    return get(this.props, 'listing.taxIncluded', false);
  }

  get taxIncludedHint(): string {
    return get(this.props, 'listing.taxIncludedHint', '');
  }

  get showIncludedTax(): boolean {
    return this.taxIncluded && this.taxIncludedHint !== '';
  }

  get isLive(): boolean {
    return get(this.props, 'listing.state', '') === 'live';
  }

  isAuction(): boolean {
    return this.props.listing.listingType === core_apimessages_Listing_ListingType.AUCTION;
  }

  hasDiscount(): boolean {
    return !!this.props.listing.pricing.originalPrice;
  }

  ribbonText(): string {
    return this.props.listing.pricing.ribbon ?
      this.props.listing.pricing.ribbon.display :
      null;
  }

  renderOriginalPrice() {
    if (this.hasDiscount() && this.isLive) {
      return (
        <div className="price-with-shipping__price__original">
          <span className="visually-hidden">{I18n.t('commons.priceWithShipping.originalPrice')}</span>
          {this.props.listing.pricing.originalPrice.display}
        </div>
      );
    }

    return null;
  }

  renderPrice() {
    const hiddenText = this.hasDiscount()
      ? I18n.t('commons.priceWithShipping.newPrice')
      : I18n.t('commons.priceWithShipping.price');

    return (
      <>
        <span className="visually-hidden">{hiddenText}</span>
        <PriceDisplay
          amountCents={this.props.listing.pricing.buyerPrice.amountCents}
          currency={this.props.listing.pricing.buyerPrice.currency}
          display={this.props.listing.pricing.buyerPrice.display}
          auction={this.isAuction()}
        />
      </>
    );
  }

  // Calculates what the buyer's running subtotal will
  // be with this listing's shop, if they were to add to cart.
  shopSubtotalForListingCents() {
    const { cartItems, listing } = this.props;
    const { shop } = listing;
    const buyerPriceCents = listing.pricing.buyerPrice.amountCents;
    if (!cartItems?.length) { return buyerPriceCents; }

    let shopSubtotalCents = 0;
    cartItems.forEach(item => {
      if (item.shopId === shop.id) {
        shopSubtotalCents += item.subtotal.amountCents;
      }
    });

    // NB: if the listing is already in the cart, clicking 'add to cart' does not
    // increase the quantity – therefore, only add the current listing's
    // amount to the subtotal if its not in the cart already
    if (!cartItems.some(item => item.listingId === listing.id)) {
      shopSubtotalCents += buyerPriceCents;
    }
    return shopSubtotalCents;
  }

  renderIncludedTax() {
    if (!this.showIncludedTax) {
      return null;
    }

    return (
      <div className="price-with-shipping__included-tax">
        {this.taxIncludedHint}
      </div>
    );
  }

  renderAcceptsOffers() {
    if (this.props.listing.listingType !== core_apimessages_Listing_ListingType.MAKE_AN_OFFER) {
      return null;
    }

    return (
      <div className="price-with-shipping__accepts-offers">
        {I18n.t('commons.acceptsOffers')}
      </div>
    );
  }

  renderRibbon() {
    if (!this.isLive) { return null; }
    return (
      <div className="price-with-shipping__discount">
        <RibbonView
          auction={this.isAuction()}
          listingStateDescription={this.props.listing.stateDescription}
          listingStateSlug={this.props.listing.state}
          discountDisplay={this.ribbonText()}
          unstyled
        />
      </div>
    );
  }

  render() {
    return (
      <div className="price-with-shipping">
        <div className="price-with-shipping__price">
          {this.renderOriginalPrice()}
          <div className="price-with-shipping__price__amount">
            {this.renderPrice()}
            {this.props.inlineRibbon && this.renderRibbon()}
            {this.renderIncludedTax()}
          </div>
        </div>

        <ListingShippingDisplay
          listing={this.props.listing}
          shopSubtotalForListingCents={this.shopSubtotalForListingCents()}
          showCombinedRate={this.props.showCombinedRate}
          useCombinedShippingStrikethrough={this.props.useCombinedShippingStrikethrough}
        />

        {!this.props.inlineRibbon && this.renderRibbon()}
      </div>
    );
  }
}

export default withUserContext(PriceWithShipping);
